import React, { useState, useEffect } from 'react';
import { useTenant } from '../context/TenantContext';
import patientTypeService, { PatientType } from '../services/patientTypeService';

export const PatientTypesPage: React.FC = () => {
  const { currentTenant } = useTenant();
  const [types, setTypes] = useState<PatientType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Form state
  const [name, setName] = useState('');

  const loadTypes = React.useCallback(async () => {
    if (!currentTenant) return;
    try {
      setIsLoading(true);
      const data = await patientTypeService.getAll(currentTenant.id);
      setTypes(data);
      setError(null);
    } catch (err) {
      console.error('Failed to load patient types:', err);
      setError('Failed to load patient types');
    } finally {
      setIsLoading(false);
    }
  }, [currentTenant]);

  useEffect(() => {
    loadTypes();
  }, [loadTypes]);

  const handleAdd = () => {
    setIsAdding(true);
    setEditingId(null);
    setName('');
  };

  const handleEdit = (type: PatientType) => {
    setEditingId(type.id);
    setIsAdding(false);
    setName(type.name);
  };

  const handleSave = async () => {
    if (!currentTenant || !name.trim()) return;

    try {
      if (isAdding) {
        await patientTypeService.create(currentTenant.id, { name });
      } else if (editingId) {
        await patientTypeService.update(editingId, { name });
      }
      await loadTypes();
      handleCancel();
    } catch (err) {
      console.error('Failed to save patient type:', err);
      setError('Failed to save patient type');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this patient type?')) return;

    try {
      await patientTypeService.delete(id);
      await loadTypes();
    } catch (err: any) {
      console.error('Failed to delete patient type:', err);
      if (err.response?.status === 409) {
        setError('Cannot delete: patient type is in use by patients or fields.');
      } else {
        setError('Failed to delete patient type');
      }
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setName('');
  };

  if (isLoading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Patient Types</h1>
          <button
            onClick={handleAdd}
            className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
          >
            + Add Patient Type
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
            {error}
          </div>
        )}

        {/* Add/Edit Form */}
        {(isAdding || editingId) && (
          <div className="mb-6 p-4 border border-gray-300 rounded-md bg-gray-50">
            <h3 className="text-lg font-semibold mb-4">
              {isAdding ? 'Add New Patient Type' : 'Edit Patient Type'}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g. Adult, Child"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
              >
                Save
              </button>
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Patient Types Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {types.length === 0 ? (
                <tr>
                  <td colSpan={2} className="px-6 py-4 text-center text-gray-500">
                    No patient types found. Click "Add Patient Type" to create one.
                  </td>
                </tr>
              ) : (
                types.map((type) => (
                  <tr key={type.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {type.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(type)}
                        className="text-teal-600 hover:text-teal-900 mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(type.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
